/** Local PNG fallbacks for grow/trio icons (Lottie JSON comes from Firebase). */
const LARGE_ICONS_ROOT =
  "game_page_animation/Large prize icons (for grow animation)";

/** Encode each path segment so spaces/parentheses work in URLs. */
export function toAssetUrl(...segments: string[]): string {
  return (
    "assets/" +
    segments
      .join("/")
      .split("/")
      .map((s) => encodeURIComponent(s))
      .join("/")
  );
}

const TWINKLE_LOCAL = toAssetUrl(LARGE_ICONS_ROOT, "twinkle-500.png");

export type GameHudPrizeType =
  | "gems"
  | "points"
  | "credit"
  | "cash"
  | "rapid_fire_token"
  | "star_token"
  | "winning_balance"
  | "free_play"
  | "prize_unlocker";

/** Entry inside `Global_lookup/animations_new/{prize}.animation[]`. */
export interface AnimationsNewRangeEntry {
  lottie_url?: string;
  range?: { min?: number; max?: number };
}

export interface AnimationsNewPrizeNode {
  animation?: AnimationsNewRangeEntry[] | Record<string, AnimationsNewRangeEntry>;
  icon_url?: string;
  lottie_url?: string;
}

export type AnimationsNewDoc = Record<string, AnimationsNewPrizeNode | unknown> & {
  twinkle?: { icon_url?: string };
};

export interface ResolvedPrizeAssets {
  lottieUrl: string | null;
  iconUrl: string | null;
}

/**
 * Map HUD / presentation queue keys → `Global_lookup/animations_new` field names.
 */
export function mapHudTypeToAnimationsKey(type: string): string {
  switch (type) {
    case "free_play":
      return "free_game";
    case "rapid_fire_token":
      return "rapid_fire";
    case "cash":
      return "winning_balance";
    default:
      return type;
  }
}

/** Alternate `animations_new` keys to try when the primary mapping has no lottie. */
export function animationDocKeysForHudType(type: string): string[] {
  switch (type) {
    case "free_play":
      return ["free_game", "free_play"];
    case "cash":
      return ["winning_balance", "cash"];
    case "winning_balance":
      return ["winning_balance", "cash"];
    case "rapid_fire_token":
      return ["rapid_fire", "rapid_fire_token"];
    default:
      return [mapHudTypeToAnimationsKey(type)];
  }
}

/**
 * Credit / cash / winning_balance fly to the prize-unlocker badge (not the avatar).
 */
export function fliesToPrizeUnlocker(type: string): boolean {
  const t = type as GameHudPrizeType;
  return t === "credit" || t === "cash" || t === "winning_balance";
}

/**
 * Unlocker key animation is only for cash / credit prizes (HATDEV-90338 demo).
 * Non-cash/CR unlocker fills must not play the key flyout.
 */
export function shouldPlayPrizeUnlockerKeyAnimation(
  presentation: readonly string[],
  lastAward?: Record<string, unknown> | null
): boolean {
  if (
    presentation.some(
      (t) => t === "credit" || t === "cash" || t === "winning_balance"
    )
  ) {
    return true;
  }
  if (!lastAward || typeof lastAward !== "object") {
    return false;
  }
  return (
    (Number(lastAward["credit"]) || 0) > 0 ||
    (Number(lastAward["cash"]) || 0) > 0
  );
}

/**
 * Free play flies into the profile avatar.
 */
export function fliesToAvatar(type: GameHudPrizeType): boolean {
  return type === "free_play";
}

export interface HudAnimationEntry {
  animated?: boolean;
  previous?: number;
  /** Explicit won amount when BE provides it (optional). */
  delta?: number;
  amount?: number;
  won?: number;
  token_count?: number;
  /** Where the prize came from — challenge rewards use a separate animation. */
  source?: string;
  origin?: string;
  from?: string;
  updated_timestamp?: string;
}

export interface RapidFireTokenAnimationDoc {
  processed?: boolean;
  token_count?: number;
  lottie_url?: string;
}

/**
 * Read the live amount from `users/.../balance/current` for a HUD presentation type.
 * Queue keys can differ from Firestore field names (e.g. rapid_fire_token → rapid_fire.tokens).
 */
export function resolveBalanceAmountForHudType(
  balance: Record<string, unknown> | null | undefined,
  type: string
): number | undefined {
  if (!balance) {
    return undefined;
  }

  const readNumber = (value: unknown): number | undefined => {
    if (value === undefined || value === null) {
      return undefined;
    }
    const n = Number(value);
    return Number.isFinite(n) ? n : undefined;
  };

  const preferred =
    balance["preferred_currency"] && typeof balance["preferred_currency"] === "object"
      ? (balance["preferred_currency"] as Record<string, unknown>)
      : null;

  switch (type as GameHudPrizeType) {
    case "rapid_fire_token": {
      const nested = balance["rapid_fire"];
      if (nested && typeof nested === "object") {
        const tokens = readNumber((nested as Record<string, unknown>).tokens);
        if (tokens !== undefined) {
          return tokens;
        }
      }
      return readNumber(balance[type]);
    }
    case "cash":
    case "winning_balance": {
      return (
        readNumber(balance["winning_balance"]) ??
        readNumber(preferred?.["winning_balance"]) ??
        readNumber(balance["cash"]) ??
        readNumber(preferred?.["cash"])
      );
    }
    case "credit": {
      return (
        readNumber(balance["credit"]) ??
        readNumber(preferred?.["credit"])
      );
    }
    case "free_play": {
      return (
        readNumber(balance["free_play"]) ??
        readNumber(balance["free_game"]) ??
        readNumber(preferred?.["free_play"]) ??
        readNumber(preferred?.["free_game"])
      );
    }
    default:
      return readNumber(balance[type]);
  }
}

/** Animation state keys that may hold `previous` for a presentation queue type. */
export function animationStateKeysForHudType(type: string): string[] {
  const keys = [type];
  if (type === "rapid_fire_token") {
    keys.push("rapid_fire");
  } else if (type === "cash") {
    keys.push("winning_balance");
  } else if (type === "free_play") {
    keys.push("free_game");
  }
  return keys;
}

export function resolveAnimationEntryForHudType(
  animationState: Record<string, HudAnimationEntry> | null | undefined,
  type: string
): HudAnimationEntry | undefined {
  if (!animationState) {
    return undefined;
  }
  for (const key of animationStateKeysForHudType(type)) {
    const entry = animationState[key];
    if (entry && typeof entry === "object") {
      return entry;
    }
  }
  return undefined;
}

/**
 * True while this prize type is still waiting for / playing its HUD flyout.
 * Live balance is already updated in Firestore — HUD should keep showing `previous`
 * until the type leaves `presentation`.
 */
export function isHudTypePendingAnimation(
  gameHud: {
    animate?: boolean;
    presentation?: string[];
    animations?: Record<string, HudAnimationEntry>;
  } | null | undefined,
  type: string
): boolean {
  if (!gameHud) {
    return false;
  }

  const presentation = Array.isArray(gameHud.presentation) ? gameHud.presentation : [];
  for (const key of animationStateKeysForHudType(type)) {
    if (presentation.includes(key)) {
      return true;
    }
  }

  const entry = resolveAnimationEntryForHudType(gameHud.animations, type);
  return !!(gameHud.animate && entry?.animated === true);
}

/**
 * Amount to render on the powerbar/HUD while a prize animation is pending.
 * Prefers `animations.*.previous`, then (for RF) live − unprocessed won count.
 */
export function resolveDeferredHudDisplayAmount(
  type: string,
  liveAmount: number,
  gameHud: {
    animate?: boolean;
    presentation?: string[];
    animations?: Record<string, HudAnimationEntry>;
  } | null | undefined,
  rapidFireTokenAnimation?: RapidFireTokenAnimationDoc | null
): number {
  const live = Number.isFinite(liveAmount) ? liveAmount : 0;
  if (!isHudTypePendingAnimation(gameHud, type)) {
    return live;
  }

  const entry = resolveAnimationEntryForHudType(gameHud?.animations, type);
  const previous = Number(entry?.previous);
  if (Number.isFinite(previous)) {
    return previous;
  }

  if (type === "rapid_fire_token") {
    const won = Number(rapidFireTokenAnimation?.token_count);
    if (
      rapidFireTokenAnimation &&
      rapidFireTokenAnimation.processed !== true &&
      Number.isFinite(won) &&
      won > 0
    ) {
      return Math.max(0, live - won);
    }
  }

  return live;
}

/**
 * Prize-box (`star_token`) from challenge completion uses a separate animation.
 * Only in-game star tokens belong in the post-game trio / flyout sequence.
 */
export function isIncludedInGamePrizeSequence(
  type: string,
  entry: HudAnimationEntry | undefined,
  animateChallenge: boolean
): boolean {
  if (type !== "star_token") {
    return true;
  }

  const source = String(entry?.source ?? entry?.origin ?? entry?.from ?? "")
    .trim()
    .toLowerCase();

  if (source) {
    if (source.includes("challenge")) {
      return false;
    }
    if (
      source.includes("game") ||
      source === "in_game" ||
      source === "ingame" ||
      source === "gameplay"
    ) {
      return true;
    }
  }

  // No explicit source: while challenge HUD animation is pending, leave prizebox
  // tokens for that flow instead of the in-game prize sequence.
  if (animateChallenge) {
    return false;
  }

  return true;
}

/**
 * Won amount for HUD prize pop-up / flyout.
 * Prefer an explicit win amount (RF token_count / animation delta) over balance totals.
 */
export function resolveHudWinDelta(
  type: string,
  balance: Record<string, unknown> | null | undefined,
  animationState: Record<string, HudAnimationEntry> | null | undefined,
  rapidFireTokenAnimation?: RapidFireTokenAnimationDoc | null
): number {
  // Rapid Fire: dedicated animation doc stores the tokens won this session.
  if (type === "rapid_fire_token") {
    const won = Number(rapidFireTokenAnimation?.token_count);
    if (
      rapidFireTokenAnimation &&
      rapidFireTokenAnimation.processed !== true &&
      Number.isFinite(won) &&
      won > 0
    ) {
      return won;
    }
  }

  const entry = resolveAnimationEntryForHudType(animationState, type);
  const explicitWon = Number(
    entry?.delta ?? entry?.amount ?? entry?.won ?? entry?.token_count
  );
  if (
    entry &&
    (entry.delta != null ||
      entry.amount != null ||
      entry.won != null ||
      entry.token_count != null) &&
    Number.isFinite(explicitWon) &&
    explicitWon !== 0
  ) {
    return Math.abs(explicitWon);
  }

  const previous = Number(entry?.previous ?? 0);
  const resolved = resolveBalanceAmountForHudType(balance, type);
  const current = resolved ?? previous;
  const delta = current - (Number.isFinite(previous) ? previous : 0);
  return Number.isFinite(delta) ? delta : 0;
}

/**
 * Select the animation whose `range.min`/`range.max` contains the won amount.
 * Inclusive on both ends. Falls back to the last entry when nothing matches.
 */
export function pickAnimationByRange(
  entries: AnimationsNewRangeEntry[] | null | undefined,
  amount: number
): AnimationsNewRangeEntry | null {
  if (!entries?.length) {
    return null;
  }
  const value = Math.abs(Number(amount) || 0);
  const match = entries.find((entry) => {
    const min = Number(entry?.range?.min ?? Number.NEGATIVE_INFINITY);
    const max = Number(entry?.range?.max ?? Number.POSITIVE_INFINITY);
    return value >= min && value <= max;
  });
  return match ?? entries[entries.length - 1] ?? null;
}

/**
 * Resolve lottie + grow icon from `/Global_lookup/animations_new` using range rules.
 */
export function resolvePrizeAssetsFromDoc(
  animationsNew: AnimationsNewDoc | Record<string, unknown> | null | undefined,
  type: string,
  amount: number
): ResolvedPrizeAssets {
  if (!animationsNew || typeof animationsNew !== "object") {
    return { lottieUrl: null, iconUrl: null };
  }

  // Prefer a lottie from any candidate key; keep the first icon as fallback so an
  // icon-only primary mapping does not skip alternate keys that have a lottie.
  let fallbackIconUrl: string | null = null;

  for (const key of animationDocKeysForHudType(type)) {
    const node = animationsNew[key] as AnimationsNewPrizeNode | undefined;
    if (!node || typeof node !== "object") {
      continue;
    }

    const iconUrl = typeof node.icon_url === "string" ? node.icon_url : null;
    if (!fallbackIconUrl && iconUrl) {
      fallbackIconUrl = iconUrl;
    }

    // Flat types (e.g. prize_unlocker) expose lottie_url on the node itself.
    if (typeof node.lottie_url === "string" && node.lottie_url) {
      return { lottieUrl: node.lottie_url, iconUrl: iconUrl ?? fallbackIconUrl };
    }

    const picked = pickAnimationByRange(normalizeAnimationEntries(node.animation), amount);
    const lottieUrl =
      typeof picked?.lottie_url === "string" && picked.lottie_url
        ? picked.lottie_url
        : null;

    if (lottieUrl) {
      return { lottieUrl, iconUrl: iconUrl ?? fallbackIconUrl };
    }
  }

  return { lottieUrl: null, iconUrl: fallbackIconUrl };
}

/** Firestore may return `animation` as an array or as a numeric-keyed map. */
function normalizeAnimationEntries(
  animation: AnimationsNewRangeEntry[] | Record<string, AnimationsNewRangeEntry> | null | undefined
): AnimationsNewRangeEntry[] {
  if (Array.isArray(animation)) {
    return animation.filter(Boolean);
  }
  if (animation && typeof animation === "object") {
    return Object.keys(animation)
      .sort((a, b) => Number(a) - Number(b))
      .map((key) => animation[key])
      .filter(Boolean);
  }
  return [];
}

export function resolveTwinkleIconUrl(
  animationsNew: AnimationsNewDoc | Record<string, unknown> | null | undefined
): string {
  const twinkle = animationsNew?.["twinkle"] as { icon_url?: string } | undefined;
  if (typeof twinkle?.icon_url === "string" && twinkle.icon_url) {
    return twinkle.icon_url;
  }
  return TWINKLE_LOCAL;
}

/** Local PNG fallback for grow/trio icons when Firebase `icon_url` is missing. */
export function resolveLargeGrowIconPath(type: string): string | null {
  const t = type as GameHudPrizeType;
  const base = LARGE_ICONS_ROOT;
  switch (t) {
    case "points":
      return toAssetUrl(base, "points-large-shad.png");
    case "gems":
      return toAssetUrl(base, "ruby-large-shad.png");
    case "star_token":
      return toAssetUrl(base, "pb-token-large-shad.png");
    case "rapid_fire_token":
      return toAssetUrl(base, "free-play-icon-720.png");
    case "credit":
      return toAssetUrl(base, "credit-large-shad.png");
    case "cash":
    case "winning_balance":
      return toAssetUrl(base, "cash-large-shad.png");
    case "free_play":
      return toAssetUrl(base, "free-play-large-shad.png");
    case "prize_unlocker":
      return toAssetUrl(base, "unlocker-key.png");
    default:
      return null;
  }
}

/**
 * Lottie + icon URLs from Firebase `animations_new`.
 * Icon falls back to local grow PNGs when `icon_url` is missing.
 */
export function resolvePrizeAssets(
  animationsNew: AnimationsNewDoc | Record<string, unknown> | null | undefined,
  type: string,
  amount: number
): ResolvedPrizeAssets {
  const fromDoc = resolvePrizeAssetsFromDoc(animationsNew, type, amount);
  return {
    lottieUrl: fromDoc.lottieUrl,
    iconUrl: fromDoc.iconUrl ?? resolveLargeGrowIconPath(type),
  };
}

export function formatPrizeLabel(
  type: string,
  delta: number,
  currencyCode: string
): string {
  const t = type as GameHudPrizeType;
  const d = Math.abs(delta);
  if (t === "credit" || t === "cash" || t === "winning_balance") {
    return formatMoney(delta, currencyCode);
  }
  if (t === "free_play") {
    return `×${Math.round(d) || 1}`;
  }
  return `×${formatInt(d)}`;
}

/** Text shown on the flying Lottie (includes + for monetary wins). */
export function formatFlyoutLabel(
  type: string,
  delta: number,
  currencyCode: string
): string {
  const t = type as GameHudPrizeType;
  const base = formatPrizeLabel(type, delta, currencyCode);
  if (t === "credit" || t === "cash" || t === "winning_balance") {
    return `+${base}`;
  }
  return base;
}

function formatInt(n: number): string {
  const v = Math.round(Math.abs(n));
  return v.toLocaleString("en-GB", { maximumFractionDigits: 0 });
}

function formatMoney(amount: number, currencyCode: string): string {
  const a = Math.abs(amount);
  try {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: currencyCode || "GBP",
      maximumFractionDigits: 2,
    }).format(a);
  } catch {
    if (currencyCode === "EUR") return `€${a.toFixed(2)}`;
    return `£${a.toFixed(2)}`;
  }
}
